import { useEffect, useState } from "react";
import yearlyEventsService, { premiosFestaJunina } from "../../../../domain/services/marketing/events/yearly.events.service";
import useRequest from "../../../../hooks/useRequest";
import transactionService from "../../../../domain/services/financial/transaction.service";
import loginService from "../../../../domain/services/login.service";
import LoadingSpin from "../../../../components/LoadingSpin";
import { ArrowRight, AtSign, Instagram, Smartphone, Star, Video } from "lucide-react";
import GiftBox from "../../../../components/marketing/blog/events/christmas/giftBox";
import { christmas2025Prizes } from "../../../../domain/services/marketing/events/events.configs/event.christmas.2025.config";
import datetimeProvider from "../../../../providers/datetime.provider";
import { shuffleArray } from "../../../../components/providers/utils.provider";
import ChristmasPrizeModal from "../../../../components/marketing/blog/events/christmas/christmas.prize.modal";
import { useHistory } from "react-router-dom";

function ChristmasTreeIcon() {
    return (
        <div className="relative">
            <svg width="48" height="56" viewBox="0 0 48 56" className="drop-shadow-md">
                {/* Arvore */}
                <path d="M24 4 L18 16 L21 16 L14 28 L18 28 L10 42 L38 42 L30 28 L34 28 L27 16 L30 16 Z" fill="#1e5a3c" />
                {/* Tronco */}
                <rect x="20" y="42" width="8" height="10" rx="1" fill="#8B5A2B" />
                {/* Estrela */}
                <polygon
                    points="24,2 25.5,6 29,6 26,8.5 27.5,12 24,10 20.5,12 22,8.5 19,6 22.5,6"
                    fill="#d4a843"
                    className="animate-star-twinkle"
                />
                {/* Bolinhas */}
                <circle cx="21" cy="20" r="2" fill="#c41e3a" />
                <circle cx="27" cy="26" r="2" fill="#d4a843" />
                <circle cx="19" cy="34" r="2" fill="#c41e3a" />
                <circle cx="29" cy="36" r="2" fill="#d4a843" />
            </svg>
        </div>
    )
}

export default function PostPurchaseChristmasTree({
    onClose = () => { },
    isInModal = false
}) {

    const history = useHistory();

    const moment = datetimeProvider.momentInstance();

    const user = loginService.getUserAuthentication();

    const currentEvent = yearlyEventsService.getCurrentEvent();

    const [findCustomerTransactions] = useRequest(transactionService.findByCustomerId);
    const [addTransactionPrize, isAddingTransactionPrize] = useRequest(transactionService.addTransactionPrize);

    const [loading, setLoading] = useState(true);
    const [lastTransaction, setLastTransaction] = useState(null);
    const [prizes, setPrizes] = useState([]);
    const [selectedPrize, setSelectedPrize] = useState(null);
    const [showPrizeModal, setShowPrizeModal] = useState(false);

    const shufflePrizes = (transaction) => {
        const packagePrizes = christmas2025Prizes.filter(prize => prize.package.includes(transaction?.additionalInformation?.marketingProductId))
        const shuffledPrizes = shuffleArray(packagePrizes);

        setPrizes(shuffledPrizes);
    }

    const checkToShow = async () => {
        setLoading(true);

        if (!currentEvent || currentEvent.key !== yearlyEventsService.yearlyEvents.christmas2025.key)
            return false;

        const transactions = await findCustomerTransactions(user.customer.id);

        const lastTransaction = transactions[0] || null;

        if (!lastTransaction || moment(lastTransaction.created).isBefore('2025-12-01 00:00:00'))
            return false;

        const prizesWon = lastTransaction?.additionalInformation?.roulettePrizes || [];

        const canSelectGift = prizesWon.length < (lastTransaction?.additionalInformation?.packageAmount || 1);

        if (!canSelectGift)
            return false;

        setLastTransaction(lastTransaction);
        setLoading(false);

        shufflePrizes(lastTransaction);

        return true;
    }

    useEffect(() => {
        checkToShow()
            .then(shouldShow => {
                if (!shouldShow)
                    onClose();
            });
    }, []);

    const handleSelectPrize = (prize) => {
        console.log(prize);

        if (selectedPrize && selectedPrize.key !== prize.key)
            return;

        setShowPrizeModal(true);

        if (selectedPrize)
            return;

        setSelectedPrize(prize);
        addTransactionPrize(lastTransaction.id, prize.key, 'christmas');
    }

    const renderIphonePromoCard = () => (
        <div className="relative bg-white border border-black/[0.06] rounded-[20px] p-5 md:p-6 shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden">
            {/* Linha dourada superior */}
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#d4a843]/60 to-transparent" />

            {/* Header */}
            <div className="flex items-center gap-3 mb-5">
                {/* Icone iPhone em outline dourado */}
                <div className="w-11 h-11 border-2 border-[#d4a843] rounded-[14px] flex items-center justify-center shrink-0 bg-[#d4a843]/5">
                    <Smartphone className="w-5 h-5 text-[#d4a843]" strokeWidth={2} />
                </div>
                <div>
                    <h3
                        className="text-[#1a1a1a] font-semibold text-sm"
                        style={{ fontFamily: "var(--font-poppins), Poppins, sans-serif" }}
                    >
                        Concorra a um <span className="text-[#c41e3a]">Super iPhone</span>
                    </h3>
                    <p className="text-[#5A5A5A] text-xs">Sorteio: 24/12/2025</p>
                </div>
            </div>

            {/* Passos */}
            <div className="space-y-3">
                <div className="flex items-center gap-3 text-xs">
                    <div className="w-7 h-7 bg-[#1e5a3c]/10 rounded-[10px] flex items-center justify-center shrink-0">
                        <Video className="w-4 h-4 text-[#1e5a3c]" />
                    </div>
                    <span className="text-[#5A5A5A]">Filme sua experiência escolhendo o presente</span>
                </div>

                <div className="flex items-center gap-3 text-xs">
                    <div className="w-7 h-7 bg-[#1e5a3c]/10 rounded-[10px] flex items-center justify-center shrink-0">
                        <AtSign className="w-4 h-4 text-[#1e5a3c]" />
                    </div>
                    <span className="text-[#5A5A5A]">Grave um depoimento sobre a Facilita</span>
                </div>

                <div className="flex items-center gap-3 text-xs">
                    <div className="w-7 h-7 bg-[#1e5a3c]/10 rounded-[10px] flex items-center justify-center shrink-0">
                        <Instagram className="w-4 h-4 text-[#1e5a3c]" />
                    </div>
                    <span className="text-[#5A5A5A]">
                        Poste no Instagram e marque <span className="text-[#c41e3a] font-medium">@facilita.juridico</span>
                    </span>
                </div>
            </div>
        </div>
    )

    return (
        <div>
            {loading ? (
                <div className="w-full py-64 flex flex-col gap-3 items-center">
                    <LoadingSpin size="large" />
                    <div className="text-primary text-base font-semibold">Carregando...</div>
                </div>
            ) : (
                <div
                    className="bg-repeat min-h-full w-full"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23d4c4a8' stroke-width='1' opacity='0.5'%3E%3Cpath d='M20 15 L15 25 L25 25 Z'/%3E%3Cpath d='M20 22 L12 35 L28 35 Z'/%3E%3Crect x='18' y='35' width='4' height='5'/%3E%3Ccircle cx='20' cy='12' r='2'/%3E%3Cline x1='60' y1='15' x2='60' y2='35'/%3E%3Cline x1='50' y1='25' x2='70' y2='25'/%3E%3Ccircle cx='60' cy='25' r='3'/%3E%3Ccircle cx='100' cy='20' r='8'/%3E%3Ccircle cx='100' cy='35' r='10'/%3E%3Ccircle cx='98' cy='18' r='1'/%3E%3Ccircle cx='102' cy='18' r='1'/%3E%3Crect x='50' y='70' width='20' height='16' rx='2'/%3E%3Crect x='50' y='65' width='20' height='6' rx='1'/%3E%3Cline x1='60' y1='65' x2='60' y2='86'/%3E%3Cpath d='M20 70 L15 80 L25 80 Z'/%3E%3Cpath d='M20 77 L12 90 L28 90 Z'/%3E%3Crect x='18' y='90' width='4' height='5'/%3E%3Ccircle cx='20' cy='67' r='2'/%3E%3Cline x1='100' y1='70' x2='100' y2='90'/%3E%3Cline x1='90' y1='80' x2='110' y2='80'/%3E%3Ccircle cx='100' cy='80' r='3'/%3E%3C/g%3E%3C/svg%3E")`,
                        backgroundColor: '#f5f0e8',
                        backgroundSize: '120px 120px',
                    }}
                >
                    <div className={isInModal ? "h-full" : "w-full py-8 md:px-6 md:py-12 xl:py-6"}>
                        <div className="relative z-10 w-full flex flex-col">
                            <header className="pt-8 pb-4 px-4">
                                <div className="max-w-2xl mx-auto flex flex-col items-center gap-3">
                                    <ChristmasTreeIcon />
                                    <h1
                                        className="text-2xl md:text-3xl font-semibold tracking-tight text-center"
                                        style={{ fontFamily: "var(--font-poppins), Poppins, sans-serif" }}
                                    >
                                        <span className="text-[#1e5a3c]">Natal da Facilita</span>
                                        <span className="text-[#5a5a5a] font-normal"> 2025</span>
                                    </h1>
                                </div>
                            </header>

                            <main className="flex-1 flex flex-col items-center justify-center px-4 py-6">
                                <div className="max-w-xl mx-auto w-full space-y-8">
                                    <div className="text-center">
                                        <h2
                                            className="text-xl md:text-2xl font-semibold text-[#1a1a1a] mb-3"
                                            style={{ fontFamily: "var(--font-poppins), Poppins, sans-serif" }}
                                        >
                                            Parabéns pela sua compra!
                                        </h2>
                                        <p className="text-[#5A5A5A] text-sm md:text-base leading-relaxed max-w-md mx-auto">
                                            Preparamos um presente exclusivo para impulsionar seu escritório digital.
                                        </p>
                                    </div>

                                    {/* Instrucao */}
                                    <p className="text-center text-[#1e5a3c] text-sm font-medium">Escolha uma das 5 caixas abaixo:</p>

                                    {/* Grid de presentes */}
                                    <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 py-4">
                                        {prizes.map((prize, index) => (
                                            <>
                                                <GiftBox
                                                    key={index}
                                                    prize={prize}
                                                    onClick={() => handleSelectPrize(prize)}
                                                    selectedPrize={selectedPrize}
                                                />
                                            </>
                                        ))}
                                    </div>

                                    {/* Status */}
                                    <div className="text-center">
                                        {!selectedPrize && (
                                            <p className="text-[#5A5A5A] text-sm animate-pulse-soft">Toque em um presente para revelar</p>
                                        )}
                                        {isAddingTransactionPrize && (
                                            <div className="inline-flex items-center gap-2">
                                                <div className="w-4 h-4 border-2 border-[#1e5a3c] border-t-transparent rounded-full animate-spin" />
                                                <span className="text-[#1e5a3c] text-sm">Abrindo seu presente...</span>
                                            </div>
                                        )}
                                        {selectedPrize && <p className="text-[#1e5a3c] text-sm font-medium">Presente revelado!</p>}
                                    </div>

                                    {renderIphonePromoCard()}
                                </div>

                                <button
                                    onClick={() => {
                                        if (isInModal) onClose();

                                        history.push('/home');
                                    }}
                                    className="mt-8 max-w-lg w-full bg-[#1e5a3c] hover:brightness-110 text-white font-medium py-5 rounded-[14px] shadow-md transition-all duration-300 flex items-center justify-center gap-2"
                                >
                                    Continuar usando a plataforma
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </main>

                            {/* Footer */}
                            <footer className="py-4 px-4 text-center">
                                <p className="text-[#5A5A5A] text-xs">Facilita Jurídico — Transformando a advocacia digital</p>
                            </footer>
                        </div>

                    </div>

                    <ChristmasPrizeModal
                        open={showPrizeModal}
                        prize={selectedPrize}
                        loading={isAddingTransactionPrize}
                        onClose={() => setShowPrizeModal(false)}
                    />
                </div>
            )}
        </div>
    );
}